import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const scoreBadgeClasses = (score) => {
  if (score >= 75) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (score >= 50) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const formatDate = (value) => {
  if (!value) return 'Flexible';
  try {
    return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (error) {
    return value;
  }
};

const CastingResults = () => {
  const { briefId } = useParams();
  const [brief, setBrief] = useState(null);
  const [matches, setMatches] = useState([]);
  const [shortlistedArtistIds, setShortlistedArtistIds] = useState([]);
  const [shortlists, setShortlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyShortlistId, setBusyShortlistId] = useState('');
  const [message, setMessage] = useState('');
  const [showShortlistedOnly, setShowShortlistedOnly] = useState(false);

  useEffect(() => {
    const loadResults = async () => {
      setLoading(true);
      try {
        const [matchesResponse, shortlistResponse] = await Promise.all([
          axios.get(`${API}/casting/matches/${briefId}`),
          axios.get(`${API}/casting/shortlist/${briefId}`),
        ]);
        setBrief(matchesResponse.data?.brief || null);
        setMatches(matchesResponse.data?.matches || []);
        setShortlistedArtistIds(matchesResponse.data?.shortlisted_artist_ids || []);
        setShortlists(shortlistResponse.data?.shortlists || []);
      } catch (error) {
        setMessage(error.response?.data?.detail || 'Unable to load match cards.');
      } finally {
        setLoading(false);
      }
    };
    loadResults();
  }, [briefId]);

  const headline = useMemo(() => {
    const artForm = brief?.parsed?.art_form || 'Artist';
    const location = brief?.parsed?.location || 'India';
    return `${artForm} matches for ${location}`;
  }, [brief]);

  const visibleMatches = useMemo(() => {
    if (!showShortlistedOnly) return matches;
    return matches.filter((match) => shortlistedArtistIds.includes(match.artist_id));
  }, [matches, shortlistedArtistIds, showShortlistedOnly]);

  const toggleShortlist = async (match) => {
    const isShortlisted = shortlistedArtistIds.includes(match.artist_id);
    const action = isShortlisted ? 'remove' : 'shortlist';
    setBusyShortlistId(match.artist_id);
    setMessage('');
    try {
      await axios.post(`${API}/casting/shortlist`, {
        brief_id: briefId,
        artist_id: match.artist_id,
        client_id: brief?.client_id || '',
        client_name: brief?.client_name || '',
        action,
      });
      setShortlistedArtistIds((current) =>
        isShortlisted ? current.filter((item) => item !== match.artist_id) : [...current, match.artist_id]
      );
      setShortlists((current) =>
        isShortlisted
          ? current.filter((item) => item.artist_id !== match.artist_id)
          : [
              {
                artist_id: match.artist_id,
                artist_name: match.artist_name,
                artist_slug: match.artist_slug,
                artist_category: match.artist_category,
                match_score: match.match_score,
                match_reason: match.match_reason,
              },
              ...current,
            ]
      );
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to update shortlist right now.');
    } finally {
      setBusyShortlistId('');
    }
  };

  return (
    <main className="bg-slate-50 px-4 py-10 md:px-8">
      <SEO
        title="Casting Match Results"
        description="KalaManch AI-ranked artist result cards for a live client brief."
        keywords="KalaManch casting results, artist match cards, AI artist recommendation India"
      />
      <section className="mx-auto max-w-6xl">
        <div className="rounded-[28px] border border-orange-100 bg-white p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-orange-500">Casting Results</p>
          <h1 className="mt-4 text-4xl font-black text-slate-950">{headline}</h1>
          <p className="mt-3 max-w-4xl text-lg leading-8 text-slate-600">
            Each card explains why KalaManch ranked the artist for this brief, so clients can move quickly without guessing.
          </p>
          {brief ? (
            <div className="mt-6 grid gap-4 rounded-[24px] bg-slate-50 p-6 md:grid-cols-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Art form</p>
                <p className="mt-2 font-semibold text-slate-900">{brief.parsed?.art_form || 'Artist'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Occasion</p>
                <p className="mt-2 font-semibold text-slate-900">{brief.parsed?.occasion_type || 'Opportunity'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Location</p>
                <p className="mt-2 font-semibold text-slate-900">{brief.parsed?.location || 'India'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Budget</p>
                <p className="mt-2 font-semibold text-slate-900">{brief.parsed?.budget_range || brief.parsed?.budget_inr || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Timeline</p>
                <p className="mt-2 font-semibold text-slate-900">{formatDate(brief.parsed?.event_date || brief.event_date)}</p>
              </div>
            </div>
          ) : null}
        </div>

        {message ? <div className="mt-6 rounded-2xl bg-amber-50 px-5 py-4 text-sm text-amber-800">{message}</div> : null}
        {loading ? <div className="mt-6 rounded-2xl bg-white px-6 py-10 text-center text-slate-600">Loading match cards...</div> : null}
        {!loading && !message && (
          <section className="mt-8 grid gap-6 xl:grid-cols-[1.55fr_0.95fr]">
            <div className="rounded-[28px] border border-violet-100 bg-white p-6 shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-violet-700">AI Summary</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">
                    {matches.length} match{matches.length === 1 ? '' : 'es'} found
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700">
                    {shortlistedArtistIds.length} shortlisted
                  </span>
                  <span className="rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700">
                    Top score {Math.max(...matches.map((item) => Number(item.match_score || 0)), 0)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowShortlistedOnly((current) => !current)}
                    className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                      showShortlistedOnly
                        ? 'bg-violet-700 text-white'
                        : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {showShortlistedOnly ? 'Showing shortlisted only' : 'Show shortlisted only'}
                  </button>
                </div>
              </div>
              <p className="mt-4 text-sm leading-7 text-slate-600">
                KalaManch ranked these artists using your brief, their public profile strength, category fit, city relevance, and artwork or performance context already available on the platform.
              </p>
            </div>

            <aside className="rounded-[28px] border border-emerald-100 bg-white p-6 shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-700">Shortlist</p>
              <h2 className="mt-2 text-2xl font-black text-slate-950">Saved artists for this brief</h2>
              {shortlists.length === 0 ? (
                <p className="mt-4 rounded-[22px] bg-slate-50 px-4 py-4 text-sm leading-7 text-slate-600">
                  No shortlisted artists yet. Shortlist the strongest cards first, then move into profile review and direct conversation.
                </p>
              ) : (
                <div className="mt-4 space-y-3">
                  {shortlists.slice(0, 5).map((item) => (
                    <div key={item.artist_id} className="rounded-[22px] bg-slate-50 px-4 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-base font-black text-slate-950">{item.artist_name}</p>
                          <p className="mt-1 text-sm font-semibold text-violet-700">{item.artist_category || 'Artist'}</p>
                          <p className="mt-2 text-xs leading-6 text-slate-600">{item.match_reason || 'Shortlisted for this brief.'}</p>
                        </div>
                        <span className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${scoreBadgeClasses(item.match_score)}`}>
                          {item.match_score}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Link
                          to={`/artist/${item.artist_slug || item.artist_id}`}
                          className="rounded-full border border-orange-200 px-3 py-1 text-xs font-bold text-orange-700 transition hover:bg-orange-50"
                        >
                          Open profile
                        </Link>
                        <button
                          type="button"
                          onClick={() => toggleShortlist(item)}
                          disabled={busyShortlistId === item.artist_id}
                          className="rounded-full border border-slate-200 px-3 py-1 text-xs font-bold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
                        >
                          {busyShortlistId === item.artist_id ? 'Saving...' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </section>
        )}

        {!loading && !message ? (
          <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {visibleMatches.length === 0 ? (
              <div className="md:col-span-2 xl:col-span-3 rounded-[28px] border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
                <p className="text-2xl font-black text-slate-950">
                  {showShortlistedOnly ? 'No shortlisted artists yet' : 'No strong matches yet'}
                </p>
                <p className="mt-3 text-sm leading-7 text-slate-600">
                  {showShortlistedOnly
                    ? 'Shortlist a few result cards first, then switch back here to review only the saved artists.'
                    : 'Try broadening the city, budget, or art form in your brief, or browse artists manually while the platform grows in that niche.'}
                </p>
                <div className="mt-6">
                  <Link
                    to="/artists"
                    className="inline-flex rounded-full bg-orange-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
                  >
                    Browse artists
                  </Link>
                </div>
              </div>
            ) : null}
            {visibleMatches.map((match) => (
              <article key={match.id} className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_16px_40px_rgba(15,23,42,0.06)]">
                <div className="flex items-start gap-4">
                  <img
                    src={match.artist_image_url || '/images/kalamanch-logo.jpg'}
                    alt={match.artist_name}
                    className="h-20 w-20 rounded-2xl object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${scoreBadgeClasses(match.match_score)}`}>
                      Match score {match.match_score}
                    </div>
                    <h2 className="mt-3 text-2xl font-black leading-tight text-slate-950">{match.artist_name}</h2>
                    <p className="mt-1 text-sm font-semibold text-violet-700">{match.artist_category || 'Artist'}</p>
                    <p className="mt-1 text-sm text-slate-500">{match.artist_city || 'India'}</p>
                    {shortlistedArtistIds.includes(match.artist_id) ? (
                      <span className="mt-3 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                        Saved in shortlist
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="mt-5 rounded-[22px] bg-orange-50 px-4 py-4 text-sm leading-7 text-slate-700">
                  <p className="font-semibold text-slate-900">Why this artist fits</p>
                  <p className="mt-2">{match.match_reason}</p>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link
                    to={`/artist/${match.artist_slug || match.artist_id}`}
                    className="rounded-full bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
                  >
                    View profile
                  </Link>
                  <Link
                    to={match.artist_slug ? `/epk/${match.artist_slug}` : `/artist/${match.artist_id}`}
                    className="rounded-full border border-orange-300 px-5 py-3 text-sm font-semibold text-orange-600 transition hover:bg-orange-50"
                  >
                    View EPK
                  </Link>
                  <button
                    type="button"
                    onClick={() => toggleShortlist(match)}
                    disabled={busyShortlistId === match.artist_id}
                    className={`rounded-full px-5 py-3 text-sm font-semibold transition ${
                      shortlistedArtistIds.includes(match.artist_id)
                        ? 'border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    {busyShortlistId === match.artist_id
                      ? 'Saving...'
                      : shortlistedArtistIds.includes(match.artist_id)
                        ? 'Remove shortlist'
                        : 'Shortlist artist'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </section>
    </main>
  );
};

export default CastingResults;
